import React, { useEffect, useMemo, useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';
import CustomModal from '../../../../util_components/custom_modal';
import Button from '../../../../util_components/button';
import { S3Handler } from '../../../../../myt_chat/worker/chatProxies';
import { S3ACL } from '../../../../../myt_chat/chat/enums/S3ACL';
import {
  broadcastErrorReason,
  fetchBroadcast,
  fetchPublicGroups,
  sendBroadcast,
  type BroadcastMedia,
  type BroadcastSummary,
  type PublicGroup
} from './broadcast_api';

interface BroadcastModalProps {
  show: boolean;
  close: () => void;
}

const MODAL_INNER: React.CSSProperties = {
  width: '90vw',
  maxWidth: 1180,
  maxHeight: '85vh',
  overflow: 'auto',
  background: 'var(--surface-card)',
  color: 'var(--text-primary)'
};

const MUTED_STYLE: React.CSSProperties = { color: 'var(--text-muted)' };

const PANE_STYLE: React.CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  background: 'var(--surface-card)'
};

const INPUT_STYLE: React.CSSProperties = {
  background: 'var(--input-bg)',
  border: '1px solid var(--input-border)',
  color: 'var(--input-text)',
  borderRadius: 4,
  padding: '6px 10px',
  width: '100%'
};

const BTN_STYLE: React.CSSProperties = { padding: '6px 14px', fontSize: '13px' };

// Image compression settings mirror the p2p chat upload path
// (components/chat/p2p_chat_window/index.tsx:882-893) but keep a larger
// maxWidthOrHeight — a broadcast image is read by many members, so 400px is too
// lossy. Output stays jpeg so the mime we report to the server is predictable.
const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1280,
  useWebWorker: true,
  fileType: 'image/jpeg'
};

// The POST only queues the fan-out, so the success pane polls GET /:uuid until
// the server has reconciled a real delivered count. The cap keeps a stuck
// reconciliation from polling forever behind an open modal.
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_MS = 5 * 60 * 1000;

const BroadcastModal: React.FC<BroadcastModalProps> = ({ show, close }) => {
  const [groups, setGroups] = useState<PublicGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState('');
  const [filter, setFilter] = useState('');
  // Selection is tracked as an explicit uuid set; `allMode` records that the
  // user asked for "All groups" so we can send target_mode:'all' (server-side
  // fan-out over every public group) instead of a snapshot list.
  const [selected, setSelected] = useState<string[]>([]);
  const [allMode, setAllMode] = useState(false);

  const [text, setText] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [compressing, setCompressing] = useState(false);

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [summary, setSummary] = useState<BroadcastSummary | null>(null);
  // Latest GET /:uuid row. Separate from `summary` so the POST reply stays the
  // source of skipped_group_uuids, which the stored row does not carry.
  const [status, setStatus] = useState<BroadcastSummary | null>(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [statusUnavailable, setStatusUnavailable] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resetAll = () => {
    setFilter('');
    setSelected([]);
    setAllMode(false);
    setText('');
    setImageFile(null);
    setImagePreview('');
    setCompressing(false);
    setSending(false);
    setSendError('');
    setSummary(null);
    setStatus(null);
    setPollTimedOut(false);
    setStatusUnavailable(false);
    setGroupsError('');
  };

  useEffect(() => {
    if (!show) return;
    resetAll();
    setGroupsLoading(true);
    fetchPublicGroups()
      .then((list) => {
        setGroups(list);
        setGroupsLoading(false);
      })
      .catch((error) => {
        setGroupsError(broadcastErrorReason(error, 'Could not load public groups.'));
        setGroupsLoading(false);
      });
  }, [show]);

  // Revoke the object URL used for the local preview when it is replaced/cleared.
  useEffect(
    () => () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    },
    [imagePreview]
  );

  // Poll the broadcast row while the success pane is open. Ends on
  // reconciled_at, on the cap, or when the modal closes / summary is cleared
  // (which tears this effect down).
  useEffect(() => {
    const uuid = summary ? summary.broadcast_uuid : '';
    if (!show || !uuid) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    const startedAt = Date.now();

    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };

    const tick = () => {
      fetchBroadcast(uuid)
        .then((row) => {
          if (cancelled) return;
          setStatus(row);
          if (row.reconciled_at) {
            stop();
          } else if (Date.now() - startedAt > POLL_MAX_MS) {
            stop();
            setPollTimedOut(true);
          }
        })
        .catch((error) => {
          if (cancelled) return;
          // 404 = goserver#964 not deployed here (or the row is gone). The send
          // itself succeeded, so degrade to the POST counters instead of
          // showing an error. Other failures are transient; the cap ends them.
          if (error && error.response && error.response.status === 404) {
            setStatusUnavailable(true);
            stop();
          } else if (Date.now() - startedAt > POLL_MAX_MS) {
            stop();
            setPollTimedOut(true);
          }
        });
    };

    timer = setInterval(tick, POLL_INTERVAL_MS);
    tick();

    return () => {
      cancelled = true;
      stop();
    };
  }, [show, summary]);

  const filteredGroups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => (g.name || '').toLowerCase().includes(q));
  }, [groups, filter]);

  const allSelected = groups.length > 0 && selected.length === groups.length;

  const toggleAll = () => {
    if (allSelected || allMode) {
      setSelected([]);
      setAllMode(false);
    } else {
      setSelected(groups.map((g) => g.group_uuid));
      setAllMode(true);
    }
  };

  const toggleGroup = (uuid: string) => {
    setSelected((prev) => {
      const next = prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid];
      // Selecting every group individually flips the master on; deselecting any
      // one flips it off (and downgrades the send to target_mode:'selected').
      setAllMode(next.length === groups.length && groups.length > 0);
      return next;
    });
  };

  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setSendError('');
    setCompressing(true);
    try {
      const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      setImageFile(compressed);
      setImagePreview(URL.createObjectURL(compressed));
    } catch (error) {
      setSendError('Could not process that image. Try a different file.');
    } finally {
      setCompressing(false);
      // Allow re-picking the same file.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview('');
  };

  const targetCount = selected.length;
  const canSend =
    (text.trim().length > 0 || !!imageFile) && targetCount > 0 && !sending && !compressing;

  const doSend = async () => {
    setSending(true);
    setSendError('');
    try {
      let media: BroadcastMedia | null = null;
      if (imageFile) {
        // Same upload API the rest of the chat uses. Private ACL: chat message
        // media is read through server-signed URLs derived from the s3 key, so
        // we keep the fileKey (s3Path) and never the transient signed url.
        const uploadResult = await S3Handler.shared.uploadFileToS3(imageFile, S3ACL.Private);
        media = {
          fdk: uploadResult.fileKey,
          mt: imageFile.type,
          fz: imageFile.size
        };
      }
      const result = await sendBroadcast({
        text: text.trim(),
        media,
        target_mode: allMode ? 'all' : 'selected',
        // In 'all' mode the server fans out over every public group itself, so
        // the explicit list is redundant — send it empty.
        group_uuids: allMode ? [] : selected
      });
      setSummary(result);
    } catch (error) {
      setSendError(broadcastErrorReason(error, 'Broadcast failed. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const doneAndClose = () => {
    resetAll();
    close();
  };

  const groupNameFor = (uuid: string) => {
    const match = groups.find((g) => g.group_uuid === uuid);
    return match ? match.name : uuid;
  };

  const renderGroupPicker = () => (
    <div className="flex flex-col" style={{ ...PANE_STYLE, minHeight: 320, maxHeight: '58vh' }}>
      <div className="p-3" style={{ borderBottom: '1px solid var(--border-default)' }}>
        <input
          style={INPUT_STYLE}
          placeholder="Filter groups by name"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleAll}
            disabled={groups.length === 0}
          />
          <span>All groups</span>
        </label>
        <div className="mt-1 text-xs" style={MUTED_STYLE}>
          {targetCount} of {groups.length} selected
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {groupsLoading && (
          <div className="p-4 text-sm" style={MUTED_STYLE}>
            Loading public groups…
          </div>
        )}
        {!groupsLoading && groupsError && (
          <div className="p-4 text-sm" style={{ color: 'var(--state-error-text)' }}>
            {groupsError}
          </div>
        )}
        {!groupsLoading && !groupsError && groups.length === 0 && (
          <div className="p-4 text-sm" style={MUTED_STYLE}>
            No public groups found.
          </div>
        )}
        {!groupsLoading && !groupsError && groups.length > 0 && filteredGroups.length === 0 && (
          <div className="p-4 text-sm" style={MUTED_STYLE}>
            No groups match “{filter}”.
          </div>
        )}
        {filteredGroups.map((group) => (
          <label
            key={group.group_uuid}
            className="flex cursor-pointer items-center gap-3 px-3 py-2"
            style={{ borderBottom: '1px solid var(--border-default)' }}
          >
            <input
              type="checkbox"
              checked={selected.includes(group.group_uuid)}
              onChange={() => toggleGroup(group.group_uuid)}
            />
            {group.avatar ? (
              <img
                className="h-8 w-8 rounded-full object-cover"
                src={group.avatar}
                alt=""
                style={{ background: 'var(--surface-sunken)' }}
              />
            ) : (
              <div
                className="h-8 w-8 rounded-full"
                style={{ background: 'var(--surface-sunken)' }}
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm">{group.name}</div>
              <div className="text-xs" style={MUTED_STYLE}>
                {group.member_count} member{group.member_count === 1 ? '' : 's'}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );

  const renderComposer = () => (
    <div className="flex flex-col gap-3 p-3" style={{ ...PANE_STYLE, minHeight: 320 }}>
      <div>
        <div className="mb-1 text-sm font-medium">Message</div>
        <textarea
          style={{ ...INPUT_STYLE, minHeight: 140, resize: 'vertical' }}
          placeholder="Type the message to broadcast…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      <div>
        <div className="mb-1 text-sm font-medium">Image (optional)</div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png"
          onChange={onPickImage}
          className="text-sm"
        />
        {compressing && (
          <div className="mt-1 text-xs" style={MUTED_STYLE}>
            Processing image…
          </div>
        )}
        {imagePreview && (
          <div className="mt-2 flex items-start gap-3">
            <img
              src={imagePreview}
              alt="attachment preview"
              style={{
                maxHeight: 140,
                maxWidth: '100%',
                borderRadius: 4,
                border: '1px solid var(--border-default)'
              }}
            />
            <Button onClick={removeImage} type="secondary" custom_style={BTN_STYLE}>
              Remove
            </Button>
          </div>
        )}
      </div>
      {sendError && (
        <div
          className="rounded border px-3 py-2 text-xs"
          style={{
            background: 'var(--state-error-bg-subtle)',
            borderColor: 'var(--state-error)',
            color: 'var(--state-error-text)'
          }}
        >
          {sendError}
        </div>
      )}
    </div>
  );

  const renderSuccess = () => {
    if (!summary) return null;
    const live = status || summary;
    const skipped = summary.skipped_group_uuids || [];
    const totalGroups = live.total_groups;
    // `accepted` is the new name for `published`; both are returned during the
    // rename, and only `published` exists on a pre-#964 goserver.
    const accepted = typeof live.accepted === 'number' ? live.accepted : live.published;
    const reconciled = !!live.reconciled_at;
    const delivered = typeof live.delivered === 'number' ? live.delivered : 0;
    const shortfall = reconciled && delivered < accepted;
    const tileClass = statusUnavailable
      ? 'mt-3 grid grid-cols-2 gap-2 text-sm md:grid-cols-4'
      : 'mt-3 grid grid-cols-2 gap-2 text-sm md:grid-cols-5';

    return (
      <div className="p-3" style={PANE_STYLE}>
        <div
          className="rounded border px-3 py-2 text-sm"
          style={{
            background: 'var(--state-success-bg)',
            borderColor: 'var(--state-success)',
            color: 'var(--state-success-text)'
          }}
        >
          {reconciled ? 'Broadcast sent.' : 'Broadcast queued — delivery status updates below.'}
        </div>
        <div className={tileClass}>
          <div>
            <div className="text-xs" style={MUTED_STYLE}>
              Total groups
            </div>
            <div>{totalGroups}</div>
          </div>
          <div>
            <div className="text-xs" style={MUTED_STYLE}>
              Accepted
            </div>
            <div>{accepted}</div>
          </div>
          {!statusUnavailable && (
            <div>
              <div className="text-xs" style={MUTED_STYLE}>
                Delivered
              </div>
              {/* Never 0 before reconciliation: a zero here reads as total failure. */}
              <div style={shortfall ? { color: 'var(--state-warning-text)' } : undefined}>
                {reconciled ? delivered : '—'}
              </div>
            </div>
          )}
          <div>
            <div className="text-xs" style={MUTED_STYLE}>
              Failed
            </div>
            <div>{live.failed}</div>
          </div>
          <div>
            <div className="text-xs" style={MUTED_STYLE}>
              Skipped
            </div>
            <div>{skipped.length}</div>
          </div>
        </div>
        {shortfall && (
          <div
            className="mt-3 rounded border px-3 py-2 text-xs"
            style={{
              background: 'var(--state-warning-bg)',
              borderColor: 'var(--state-warning)',
              color: 'var(--state-warning-text)'
            }}
          >
            {accepted - delivered} group{accepted - delivered === 1 ? '' : 's'} did not receive the
            message.
          </div>
        )}
        {!reconciled && !statusUnavailable && (
          <div className="mt-2 text-xs" style={MUTED_STYLE}>
            {pollTimedOut ? 'Still reconciling — check back later.' : 'Counting delivered groups…'}
          </div>
        )}
        <div className="mt-2 text-xs" style={MUTED_STYLE}>
          Broadcast id: {summary.broadcast_uuid}
        </div>
        {skipped.length > 0 && (
          <div className="mt-3">
            <div className="text-sm font-medium">Skipped groups</div>
            <ul className="mt-1 list-disc pl-5 text-xs" style={MUTED_STYLE}>
              {skipped.map((uuid) => (
                <li key={uuid}>{groupNameFor(uuid)}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <Button onClick={doneAndClose} custom_style={BTN_STYLE}>
            Done
          </Button>
        </div>
      </div>
    );
  };

  if (!show) return null;

  return (
    <CustomModal show={show} close={close} title="Broadcast to Groups" inner_style={MODAL_INNER}>
      {summary ? (
        renderSuccess()
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {renderGroupPicker()}
            {renderComposer()}
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button onClick={close} type="secondary" custom_style={BTN_STYLE} disabled={sending}>
              Cancel
            </Button>
            <Button onClick={doSend} disabled={!canSend} custom_style={BTN_STYLE}>
              {sending ? 'Sending…' : `Send to ${targetCount} group${targetCount === 1 ? '' : 's'}`}
            </Button>
          </div>
        </>
      )}
    </CustomModal>
  );
};

export default BroadcastModal;
